import { confirmCost } from "@/components/cost-confirm";
import { useState, useEffect, useRef } from "react";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { AgentGroup, AgentOption } from "@/components/agent-ui";
import { Link } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Globe, RefreshCw, Loader2, CalendarPlus, Check, Search, Target, ExternalLink,
  Sparkles, Trophy, ClipboardList, Layers, TrendingUp, Trash2,
} from "lucide-react";

// Liste satırı — ağır alanlar (outline, semantik set) taşınmaz, backend özet döndürür.
type BriefOzet = {
  targetKeyword: string; title: string; volume: number; globalVolume?: number; difficulty: number;
  priority: string; feasibility: string; clusterName: string | null; contentKind: string;
  intentKind?: string; approved?: boolean; hasDeepDive: boolean; runId: number; runAt: string;
};

type Brief = {
  title: string; targetKeyword: string; contentKind: "cluster" | "evergreen"; supportingKeywords: string[];
  semanticKeywords?: { term: string; type: "varyasyon" | "soru" | "varlık" | "alt-konu" }[];
  variationMetrics?: { keyword: string; role: "ana" | "varyasyon" | "destek"; volume: number; difficulty: number; cpc: number; competition: number; cluster: string | null }[];
  intentKind?: "ticari" | "bilgilendirici";
  cannibalizationCheck?: { closestExisting: string | null; verdict: "temiz" | "riskli"; note: string } | null;
  volume: number; difficulty: number; feasibility: "kolay" | "orta" | "iddialı";
  priority: "high" | "medium" | "low";
  clusterName: string | null; isNewCluster: boolean;
  competitorEvidence: { domain: string; url: string; traffic: number } | null;
  rationale: string; searchIntent: string;
  outline: { h2: string; h3: string[] }[];
  internalLinkTargets: { anchor: string; url: string }[];
  aiAngle: string;
};
type PlannerData = {
  ourDomain: string;
  siteStrength: { organicKeywords: number; organicTraffic: number; pos1: number };
  competitorsAnalyzed: { domain: string; traffic: number; topPages: { url: string; traffic: number; keywords: string[]; headings: string[] }[] }[];
  clusterGaps: { cluster: string; pageCount: number; note: string }[];
  opportunityKeywords: { keyword: string; volume: number; difficulty: number; cpc?: number; source: string; usedInBrief: boolean }[];
  briefs: Brief[];
  rejectedByAudit?: { keyword: string; reason: string; existingUrl: string | null }[];
  meta: {
    candidateKeywords: number; excludedAsTargeted: number; market: string;
    mode?: "auto" | "topic"; topic?: string | null; kindPref?: string; competitorsUsed?: string[]; count?: number;
    sources?: {
      gscConnected: boolean; gscQueries: number; targetedKeywords: number;
      inventoryPages: number; ourRankedKeywords: number; clusters: number;
      guardEliminated: number; auditEliminated: number; relevanceFiltered?: number;
      candidatePool?: number;
    };
    dfsUsd?: number; aiUsd?: number;
  };
  // Aday havuzu zayıfsa sebebini kullanıcıya söyleyen uyarı (sessiz sıfır-hacim yerine).
  poolWarning?: string | null;
};
type CompetitorSuggestion = { domain: string; commonKeywords: number; avgPosition: number };
type CompetitorData = { selected: string[]; suggestions: CompetitorSuggestion[]; fetchedAt: string | null };
type PlannerResp = {
  configured: boolean; generatedAt: string | null; data: PlannerData | null; competitorData?: CompetitorData;
  unreachable?: boolean;      // sunucuya hiç ulaşılamadı (yapılandırma sorunu DEĞİL)
  running?: boolean;           // motor arka planda çalışıyor (POST hemen döner, UI yoklar)
  runError?: string | null;    // son arka plan çalıştırması hata verdiyse mesajı
};

// Desteklenen tüm pazarlar (backend LOCALES ile eşlenik) — herhangi bir dilde analiz
const MARKETS: { k: string; l: string }[] = [
  { k: "auto", l: "🌐 Otomatik (site dili)" },
  { k: "tr", l: "🇹🇷 Türkçe" }, { k: "en", l: "🇺🇸 İngilizce (US)" }, { k: "en-gb", l: "🇬🇧 İngilizce (UK)" },
  { k: "de", l: "🇩🇪 Almanca" }, { k: "fr", l: "🇫🇷 Fransızca" }, { k: "es", l: "🇪🇸 İspanyolca" },
  { k: "it", l: "🇮🇹 İtalyanca" }, { k: "ar", l: "🇸🇦 Arapça" }, { k: "ru", l: "🇷🇺 Rusça" },
  { k: "nl", l: "🇳🇱 Felemenkçe" }, { k: "pt", l: "🇵🇹 Portekizce" }, { k: "pt-br", l: "🇧🇷 Portekizce (BR)" },
  { k: "pl", l: "🇵🇱 Lehçe" }, { k: "sv", l: "🇸🇪 İsveççe" }, { k: "el", l: "🇬🇷 Yunanca" },
  { k: "he", l: "🇮🇱 İbranice" }, { k: "ja", l: "🇯🇵 Japonca" }, { k: "ko", l: "🇰🇷 Korece" },
  { k: "zh", l: "🇹🇼 Çince" }, { k: "hi", l: "🇮🇳 Hintçe" }, { k: "id", l: "🇮🇩 Endonezce" }, { k: "az", l: "🇦🇿 Azerice" },
];

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c", blue: "#ec6461" };
const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const shortUrl = (u: string) => { try { const p = new URL(u); return p.hostname.replace(/^www\./, "") + (p.pathname.length > 1 ? p.pathname.replace(/\/$/, "") : ""); } catch { return u; } };
const feasStyle: Record<string, { c: string; l: string }> = {
  kolay: { c: "#12b76a", l: "Kolay" }, orta: { c: "#f79009", l: "Orta" }, "iddialı": { c: "#f04438", l: "İddialı" },
};
const prioStyle: Record<string, { c: string; l: string }> = {
  high: { c: "#f04438", l: "Yüksek" }, medium: { c: "#f79009", l: "Orta" }, low: { c: "#a3a3a0", l: "Düşük" },
};

export default function ContentPlanner() {
  const { data: domains } = useListDomains();
  const [activeDomainId, setActiveDomainId] = useActiveDomain(domains);
  const queryClient = useQueryClient();
  const [added, setAdded] = useState<Record<string, boolean>>({});
  const [market, setMarket] = useState<string>("auto");
  const [showAllMarkets, setShowAllMarkets] = useState(false);
  // İçerik Robotu ayarları
  const [mode, setMode] = useState<"auto" | "topic">("auto");
  const [topic, setTopic] = useState("");
  const [kind, setKind] = useState<"karma" | "ticari" | "evergreen">("karma");
  const [count, setCount] = useState(10);
  const [selectedComps, setSelectedComps] = useState<string[]>([]);
  const [manualComp, setManualComp] = useState("");
  const [compSuggestions, setCompSuggestions] = useState<CompetitorSuggestion[] | null>(null);
  const [compLoading, setCompLoading] = useState(false);
  const [compCachedAt, setCompCachedAt] = useState<string | null>(null);

  // Seçilen rakipler DOMAIN'de KALICI — her değişiklik sunucuya yazılır
  const persistComps = (list: string[]) => {
    if (!activeDomainId) return;
    fetch(`/api/domains/${activeDomainId}/competitor-selection`, {
      method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify({ selected: list }),
    }).catch(() => { /* offline'da bile UI çalışsın */ });
  };

  const suggestCompetitors = async (refresh = false) => {
    if (!activeDomainId) return;
    setCompLoading(true);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/competitor-suggestions?market=${market === "auto" ? "tr" : market}${refresh ? "&refresh=1" : ""}`);
      const j = await r.json();
      setCompSuggestions(Array.isArray(j.competitors) ? j.competitors : []);
      setCompCachedAt(j.cached ? (j.fetchedAt ?? null) : new Date().toISOString());
    } catch { setCompSuggestions([]); }
    finally { setCompLoading(false); }
  };
  const toggleComp = (dom: string) => setSelectedComps((p) => {
    const next = p.includes(dom) ? p.filter((d) => d !== dom) : p.length >= 8 ? p : [...p, dom];
    persistComps(next);
    return next;
  });
  const addManualComp = () => {
    const v = manualComp.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/.*$/, "");
    if (v.includes(".") && !selectedComps.includes(v) && selectedComps.length < 8) {
      setSelectedComps((p) => { const next = [...p, v]; persistComps(next); return next; });
    }
    setManualComp("");
  };

  const { data: planner } = useQuery({
    queryKey: ["content-planner", activeDomainId],
    enabled: !!activeDomainId,
    // Motor arka planda koşarken 5 sn'de bir yokla — bitince sonuç otomatik görünür
    refetchInterval: (q) => (q.state.data as PlannerResp | undefined)?.running ? 5000 : false,
    queryFn: async (): Promise<PlannerResp> => {
      // "Sunucuya ulaşılamadı" ile "veri motoru yapılandırılmamış" AYRI şeyler.
      // Eskiden ikisi de configured:false dönüyordu ve kullanıcı API kapalıyken
      // "yöneticiyle iletişime geç" uyarısı görüyordu — yanlış teşhise yolluyordu.
      try {
        const r = await fetch(`/api/domains/${activeDomainId}/content-planner`);
        if (!r.ok) return { configured: false, generatedAt: null, data: null, unreachable: r.status >= 500 };
        return await r.json();
      } catch { return { configured: false, generatedAt: null, data: null, unreachable: true }; }
    },
  });
  const engineBusy = !!planner?.running;
  // Domain değişince o domain'in KALICI rakip hafızasını yükle (seçim + öneriler)
  const hydratedFor = useRef<number | null>(null);
  useEffect(() => {
    if (!activeDomainId || !planner?.competitorData) return;
    if (hydratedFor.current === activeDomainId) return;
    hydratedFor.current = activeDomainId;
    setSelectedComps(planner.competitorData.selected ?? []);
    if ((planner.competitorData.suggestions?.length ?? 0) > 0) {
      setCompSuggestions(planner.competitorData.suggestions);
      setCompCachedAt(planner.competitorData.fetchedAt);
    } else {
      setCompSuggestions(null);
      setCompCachedAt(null);
    }
  }, [activeDomainId, planner]);

  // POST artık işi BAŞLATIP hemen döner ({started:true}); sonuç GET yoklamasıyla gelir
  // (uzun istek koptuğunda "çalışıyor ama sonuç yok" sorunu böyle çözüldü).
  const run = useMutation({
    mutationFn: async (): Promise<{ started: boolean }> => {
      const r = await fetch(`/api/domains/${activeDomainId}/content-planner`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ market, mode, topic: mode === "topic" ? topic : undefined, kind, count, competitors: selectedComps }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Planlama hatası");
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["content-planner", activeDomainId] });
      queryClient.invalidateQueries({ queryKey: ["planner-briefs", activeDomainId] });
    },
  });

  // Onay diyaloğu sabit bir token tahmini gösteriyordu; 1 brief ile 50 brief arasında
  // ~50 kat fark var, kullanıcı neye onay verdiğini bilsin.
  const maliyetNotu = `${count} brief üretilecek.${count >= 20 ? " Yüksek adet: uzun sürer ve token maliyeti adetle birlikte artar." : ""}`;

  // Beğenilmeyen öneriyi listeden sil (kalıcı — plan verisinden çıkarılır)
  const removeBrief = useMutation({
    mutationFn: async (targetKeyword: string): Promise<{ ok: boolean }> => {
      const r = await fetch(`/api/domains/${activeDomainId}/content-planner/brief`, {
        method: "DELETE", headers: { "content-type": "application/json" }, body: JSON.stringify({ targetKeyword }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Silinemedi");
      return r.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["content-planner", activeDomainId] }),
  });

  // Beğenilen brief → İçerik Önerileri'ndeki YAZAR HAVUZUNA (oradan tam içerik üretilir)
  const [sentToWriter, setSentToWriter] = useState<Record<string, boolean>>({});
  const sendToWriter = async (b: Brief) => {
    if (!activeDomainId) return;
    const r = await fetch(`/api/domains/${activeDomainId}/writer-briefs`, {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ brief: b }),
    });
    if (r.ok || r.status === 409) setSentToWriter((p) => ({ ...p, [b.targetKeyword]: true }));
  };

  // İçerik takvimiyle eşlenik: brief → tek tıkla plana
  const addToCalendar = async (b: Brief) => {
    if (!activeDomainId) return;
    const today = new Date().toISOString().slice(0, 10);
    const outlineTxt = b.outline.map((o) => `## ${o.h2}${o.h3.length ? "\n" + o.h3.map((h) => `  - ${h}`).join("\n") : ""}`).join("\n");
    const semTxt = (b.semanticKeywords?.length ?? 0) > 0
      ? `\n\nSemantik SEO seti:\n${(b.semanticKeywords ?? []).map((s) => `- [${s.type}] ${s.term}`).join("\n")}`
      : "";
    const notes = `${b.rationale}\n\nArama niyeti: ${b.searchIntent}\nAI/GEO açısı: ${b.aiAngle}${semTxt}\n\nÖnerilen plan:\n${outlineTxt}\n\nİç link hedefleri:\n${b.internalLinkTargets.map((l) => `- [${l.anchor}](${l.url})`).join("\n")}`;
    await fetch(`/api/domains/${activeDomainId}/content-plans`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({
        title: b.title, plannedDate: today, contentType: "Yeni blog", notes,
        sourceUrl: b.competitorEvidence?.url ?? undefined,
        keywords: [{ keyword: b.targetKeyword, usa: 0, global: b.volume, role: "ana" }, ...b.supportingKeywords.map((k) => ({ keyword: k, usa: 0, global: 0, role: "destek" }))],
      }),
    });
    setAdded((p) => ({ ...p, [b.targetKeyword]: true }));
  };

  const d = planner?.data ?? null;

  return (
    <div className="px-4 sm:px-6 py-6 mx-auto w-full max-w-[1400px]">
      {/* ── BRİEF KEŞİF AJANI — kullanıcının AI tasarımının uyarlanması ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shrink-0" style={{ background: "rgba(139,92,246,0.12)" }}>🕵️</div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground">Brief Keşif Ajanı</h1>
              {(run.isPending || engineBusy) && <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${C.cyan}18`, color: "#b12220" }}>⏳ çalışıyor</span>}
            </div>
            <p className="text-sm text-muted-foreground">
              Rakipleri analiz eder, içerik boşluklarını bulur ve yeni brief fırsatları oluşturur.
              {planner?.generatedAt && <> · Son çalıştırma: {new Date(planner.generatedAt).toLocaleDateString("tr-TR", { dateStyle: "medium" })}</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button disabled={!planner?.configured || (run.isPending || engineBusy) || (mode === "topic" && topic.trim().length < 3)} onClick={async () => { if (await confirmCost("planner", maliyetNotu)) run.mutate(); }}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
            style={{ background: "linear-gradient(135deg, #d42b29, #e83e3c)" }}>
            {(run.isPending || engineBusy) ? <Loader2 className="w-4 h-4 animate-spin" /> : <>✨</>} Briefleri Oluştur
          </button>
        </div>
      </div>

      {/* Ayar kartları — 4'lü grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-3">
        <Card><CardContent className="p-4">
          <AgentGroup icon="🧭" label="Analiz Modu">
            <AgentOption check active={mode === "auto"} onClick={() => setMode("auto")} label="✨ Akıllı Keşif" sub="Rakipler + içerik boşlukları + arama verisi" />
            <AgentOption check active={mode === "topic"} onClick={() => setMode("topic")} label="🎯 Konu Bazlı" sub="Belirli bir konu üzerinden analiz" />
          </AgentGroup>
          {mode === "topic" && (
            <>
              {/* ÇOK KONU: her satır ayrı bir odak. Havuz her konudan ayrı tohumlanır
                  ve brief'ler konular arasında dengeli dağıtılır (en fazla 5 konu). */}
              <textarea value={topic} onChange={(e) => setTopic(e.target.value)} autoFocus rows={3}
                placeholder={"Odak konu — her satıra bir konu yazabilirsin\nör. saç ekimi sonrası bakım\nör. burun estetiği iyileşme süreci"}
                className="mt-3 w-full text-sm px-3 py-2 rounded-xl border outline-none resize-y"
                style={{ borderColor: `${C.purple}66`, background: `${C.purple}06` }} />
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                {(() => {
                  const n = [...new Set(topic.split(/[\r\n;,]+/).map((t) => t.trim()).filter((t) => t.length >= 3))].length;
                  return n > 1
                    ? `${n} odak konu algılandı — brief'ler konular arasında dengeli dağıtılacak.${n >= 5 ? " (en fazla 5)" : ""}`
                    : "Birden fazla konu için her satıra bir konu yaz (virgülle de ayırabilirsin).";
                })()}
              </p>
            </>
          )}
        </CardContent></Card>

        <Card><CardContent className="p-4">
          <AgentGroup icon="🌍" label="Hedef Pazar" hint={showAllMarkets ? undefined : `Platform ${MARKETS.length - 1} dili destekler.`}>
            {(showAllMarkets ? MARKETS : MARKETS.filter((m) => ["auto", "tr", "en", "de", "es"].includes(m.k))).map((m) => (
              <AgentOption key={m.k} small check active={market === m.k} onClick={() => setMarket(m.k)} label={m.l} />
            ))}
            {!showAllMarkets && <AgentOption small active={false} onClick={() => setShowAllMarkets(true)} label="＋ Tüm Dilleri Gör" />}
          </AgentGroup>
        </CardContent></Card>

        <Card><CardContent className="p-4">
          <AgentGroup icon="⚖️" label="İçerik Stratejisi">
            <AgentOption check active={kind === "karma"} onClick={() => setKind("karma")} label="⚖️ Karma" sub="ticari + bilgi dengeli" />
            <AgentOption check active={kind === "ticari"} onClick={() => setKind("ticari")} label="💼 Ticari" sub="hizmet/satış niyetli" />
            <AgentOption check active={kind === "evergreen"} onClick={() => setKind("evergreen")} label="🌱 Evergreen" sub="zamansız bilgi içerikleri" />
          </AgentGroup>
        </CardContent></Card>

        <Card><CardContent className="p-4">
          {/* TOPLU ÜRETİM: 1'den 50'ye. 1 seçeneği tek bir konuyu hızlıca denemek için;
              yüksek adetler tek koşuda toplu brief üretmek için. */}
          <AgentGroup icon="🔢" label="Brief Sayısı" hint="Kanibalizasyon elemeleri sonrası hedef adet. Yüksek adet daha uzun sürer ve daha çok token harcar.">
            {[1, 3, 5, 10, 20, 30, 50].map((n) => (
              <AgentOption key={n} check active={count === n} onClick={() => setCount(n)} label={String(n)} />
            ))}
          </AgentGroup>
        </CardContent></Card>
      </div>

      {/* Rakip İstihbaratı */}
      <Card className="mb-4"><CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">🔎</span>
          <span className="font-semibold text-sm">Rakip İstihbaratı</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(34,197,94,0.1)", color: "#039855" }}>domain'de kalıcı</span>
          {compCachedAt && <span className="text-[10px] text-muted-foreground">keşif: {new Date(compCachedAt).toLocaleDateString("tr-TR")}</span>}
        </div>
        <p className="text-xs text-muted-foreground mb-3">Rakiplerinizden içerik fırsatları keşfedin.{mode === "auto" && selectedComps.length === 0 ? " Seçim yapmazsan en güçlü 3 rakip otomatik keşfedilir." : ""}</p>

        {selectedComps.length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Seçili Rakipler</div>
            <div className="flex items-center gap-2 flex-wrap">
              {selectedComps.map((dom) => (
                <button key={dom} onClick={() => toggleComp(dom)} className="text-xs px-3 py-1.5 rounded-xl inline-flex items-center gap-2 transition hover:opacity-75"
                  style={{ background: "rgba(139,92,246,0.08)", color: "#d42b29", border: "1px solid #e83e3c" }}>
                  <span className="w-3.5 h-3.5 rounded-full text-white text-[9px] flex items-center justify-center" style={{ background: "#ec6461" }}>✓</span>
                  {dom} <span className="opacity-50">×</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {compSuggestions && compSuggestions.filter((c) => !selectedComps.includes(c.domain)).length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Önerilen Rakipler</div>
            <div className="flex items-center gap-2 flex-wrap">
              {compSuggestions.filter((c) => !selectedComps.includes(c.domain)).slice(0, 12).map((c) => (
                <button key={c.domain} onClick={() => toggleComp(c.domain)} title={`${c.commonKeywords} ortak kelime · ort. pozisyon ${c.avgPosition}`}
                  className="text-xs px-3 py-1.5 rounded-xl inline-flex items-center gap-2 transition hover:shadow-sm border bg-white text-[#52524e] border-black/10 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700">
                  {c.domain} <span className="font-semibold text-brand-500">+</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <input value={manualComp} onChange={(e) => setManualComp(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addManualComp()}
              placeholder="Rakip site ekle…"
              className="text-sm w-full px-3.5 py-2.5 pr-9 rounded-xl border outline-none bg-white border-black/10 text-gray-800 placeholder:text-gray-400 dark:bg-gray-800 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500" />
            <button onClick={addManualComp} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-lg font-semibold text-brand-500">＋</button>
          </div>
          <button disabled={compLoading || !activeDomainId} onClick={() => suggestCompetitors(false)}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white inline-flex items-center gap-2 transition hover:opacity-90 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #d42b29, #e83e3c)" }}>
            {compLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Rakipleri Keşfet
          </button>
          <Button variant="outline" className="h-10 rounded-xl text-sm gap-2" disabled={compLoading || !compSuggestions} onClick={() => suggestCompetitors(true)} title="Rakipleri yeniden keşfet">
            <RefreshCw className="w-4 h-4" /> Yeniden Tara
          </Button>
        </div>
      </CardContent></Card>

      {/* Alt tam-genişlik çalıştırma butonu (mockup'taki gibi) */}
      <div className="flex justify-center mb-5">
        <button disabled={!planner?.configured || (run.isPending || engineBusy) || (mode === "topic" && topic.trim().length < 3)} onClick={async () => { if (await confirmCost("planner", maliyetNotu)) run.mutate(); }}
          className="w-full max-w-[560px] py-3.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(135deg, #d42b29, #e83e3c)" }}>
          {(run.isPending || engineBusy) ? <><Loader2 className="w-4 h-4 animate-spin" /> Ajan çalışıyor… (2-5 dk)</> : <>🚀 Briefleri Oluştur</>}
        </button>
      </div>

      {!planner?.configured && (
        <Card className="mb-5" style={{ borderColor: "#f7900966", background: "rgba(245,158,11,0.05)" }}><CardContent className="p-4 text-sm text-muted-foreground">
          {planner?.unreachable
            ? "Sunucuya ulaşılamıyor — bağlantını kontrol et, sorun sürerse birkaç dakika sonra tekrar dene."
            : "Geolop veri motoru yapılandırılmamış — yöneticiyle iletişime geç."}
        </CardContent></Card>
      )}
      {run.isError && <Card className="mb-5" style={{ borderColor: "#f0443866" }}><CardContent className="p-3 text-sm text-red-400">{(run.error as Error)?.message}</CardContent></Card>}
      {/* Arka plan çalıştırması hata verdiyse (motor asenkron koşuyor) */}
      {!engineBusy && planner?.runError && (
        <Card className="mb-5" style={{ borderColor: "#f0443866", background: "rgba(239,68,68,0.04)" }}><CardContent className="p-3 text-sm text-error-600 dark:text-error-400">
          ⚠️ Son çalıştırma hatayla bitti: {planner.runError} — "Motoru Çalıştır" ile tekrar dene.
        </CardContent></Card>
      )}
      {(run.isPending || engineBusy) && <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-16"><Loader2 className="w-5 h-5 animate-spin" /> Motor arka planda çalışıyor: veri toplama → stratejist → denetçi… (2-5 dk — sayfadan ayrılsan bile devam eder, bitince sonuç burada belirir)</div>}

      {!(run.isPending || engineBusy) && !d && planner?.configured && (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground"><ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-40" />Henüz çalıştırılmadı. Ayarları seç ve "Motoru Çalıştır"a bas.</CardContent></Card>
      )}

      {!(run.isPending || engineBusy) && d && (<>
        {/* HAVUZ UYARISI — aday kelime havuzu zayıf kaldıysa brief hacimleri sıfır çıkar.
            Sebebi söylemezsek kullanıcı "araç bozuk" sanıyor; asıl sebep genelde konunun
            arama verisi olmaması (cümle yazılmış ya da yazım hatası). */}
        {d.poolWarning && (
          <Card className="mb-4" style={{ borderColor: "#f7900966", background: "rgba(245,158,11,0.05)" }}><CardContent className="p-3 text-sm text-amber-600 dark:text-amber-400">
            ⚠️ {d.poolWarning}
          </CardContent></Card>
        )}
        {/* Motora Giren Veriler / Cluster Boşlukları / Rakip Sayfaları kullanıcı isteğiyle gizlendi */}
        {/* ANALİZ ÖZET KARTI — detay tıklayınca açılır (kullanıcı isteği: dümdüz dökülmesin) */}
        <Card className="mb-4" style={{ borderColor: "rgba(6,182,212,0.3)" }}><CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Link href="/planner-analysis" className="text-sm flex-1 min-w-0 cursor-pointer hover:opacity-80 transition">
              <span className="font-semibold underline decoration-dotted">📊 {planner?.generatedAt ? new Date(planner.generatedAt).toLocaleDateString("tr-TR", { dateStyle: "long" }) : ""} analizi</span>
              <span className="text-muted-foreground"> · {(MARKETS.find((m) => m.k === d.meta.market)?.l ?? d.meta.market.toUpperCase())}{d.meta.mode === "topic" && d.meta.topic ? ` · "${d.meta.topic}"` : ""} · <b className="text-foreground">{d.briefs.length} brief</b> · site gücü {fmt(d.siteStrength.organicTraffic)}/ay · {fmt(d.siteStrength.organicKeywords)} sıralı kelime · <b className="text-foreground">brief'leri gör →</b></span>
            </Link>
          </div>
        </CardContent></Card>

        {/* Fırsat Kelime Tablosu /planner-analysis sayfasına taşındı (kullanıcı isteği) */}
        {/* "Denetçinin Elediği Öneriler" bölümü kullanıcı isteğiyle kaldırıldı —
            eleme arka planda aynen çalışıyor, yalnızca listelenmiyor. */}
      </>)}

      <BriefListesi domainId={activeDomainId} />
    </div>
  );
}

/**
 * BRIEF LİSTESİ — tüm koşulardaki brief'ler tek düz listede.
 * Eskiden brief'lere ancak koşu sekmelerinin ardından (analiz sayfası → koşu seç →
 * kaydır) ulaşılıyordu; kullanıcı hepsini Brief Keşif Ajanı altında görmek istedi.
 * Sınır 100: dolunca yeni koşu reddedilir, kullanıcı silerek yer açar.
 */
function BriefListesi({ domainId }: { domainId: number | null }) {
  const qc = useQueryClient();
  const [silinen, setSilinen] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ["planner-briefs", domainId],
    enabled: !!domainId,
    queryFn: async (): Promise<{ briefs: BriefOzet[]; total: number; limit: number }> => {
      try { const r = await fetch(`/api/domains/${domainId}/planner-briefs`); return r.ok ? await r.json() : { briefs: [], total: 0, limit: 100 }; } catch { return { briefs: [], total: 0, limit: 100 }; }
    },
  });
  const briefs = data?.briefs ?? [];
  const limit = data?.limit ?? 100;
  if (!domainId || briefs.length === 0) return null;

  const sil = async (kw: string, runId: number) => {
    setSilinen(kw);
    try {
      await fetch(`/api/domains/${domainId}/content-planner/brief`, {
        method: "DELETE", headers: { "content-type": "application/json" },
        body: JSON.stringify({ targetKeyword: kw, runId }),
      });
      qc.invalidateQueries({ queryKey: ["planner-briefs", domainId] });
      qc.invalidateQueries({ queryKey: ["content-planner", domainId] });
    } finally { setSilinen(null); }
  };

  const dolu = briefs.length >= limit;
  const yaklasti = briefs.length >= limit * 0.9;
  return (
    <Card className="mt-4"><CardContent className="p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <span className="font-semibold text-sm">Brief'ler</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${dolu ? "bg-brand-50 text-brand-600 dark:bg-brand-500/[0.12] dark:text-brand-400" : "bg-accent text-muted-foreground"}`}>
            {briefs.length} / {limit}
          </span>
        </div>
        {yaklasti && (
          <span className="text-[11px] text-brand-600 dark:text-brand-400">
            {dolu ? "Sınır doldu — yeni üretim için brief silin." : "Sınıra yaklaşıyorsunuz."}
          </span>
        )}
      </div>

      <div className="divide-y divide-border">
        {briefs.map((b) => (
          <div key={b.targetKeyword} className="flex items-center gap-3 py-2.5">
            <Link href={`/planner-brief/${encodeURIComponent(b.targetKeyword)}`} className="min-w-0 flex-1 cursor-pointer hover:opacity-75 transition">
              <div className="text-sm font-medium truncate">{b.title}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {b.targetKeyword}
                {b.volume > 0 ? ` · ${fmt(b.volume)}/ay` : b.globalVolume ? ` · dünya ${fmt(b.globalVolume)}/ay` : " · hacim yok"}
                {b.difficulty > 0 ? ` · zorluk ${b.difficulty}` : ""}
                {b.clusterName ? ` · ${b.clusterName}` : ""}
                {` · ${new Date(b.runAt).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })}`}
              </div>
            </Link>
            {b.approved && <span className="text-[10px] px-1.5 py-0.5 rounded shrink-0 bg-success-50 text-success-700 dark:bg-success-500/[0.12] dark:text-success-400">onaylı</span>}
            <button
              onClick={() => sil(b.targetKeyword, b.runId)}
              disabled={silinen === b.targetKeyword}
              title="Brief'i sil"
              className="shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-500/[0.12] transition disabled:opacity-40">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}
